'use client';

import { Button } from '@app/app/_components/Button/Button';
import { Countdown } from '@app/app/_components/Countdown/Countdown';
import { Input } from '@app/app/_components/Input/Input';
import { Select } from '@app/app/_components/Input/Select/Select';
import { Message } from '@app/app/_components/Message/Message';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { SpinnerIcon } from '@app/app/_icons/SpinnerIcon';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { getSmartIdStatus } from '@app/service/auth/actions/getSmartIdStatus';
import { loginWithSmartId } from '@app/service/auth/actions/loginWithSmartId';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormState } from 'react-dom';

interface Props {
    onSuccess?: () => void;
    domain?: string;
    disableAutoFill?: boolean;
    disablePasting?: boolean;
}

export function SmartIdLoginSection({ onSuccess, domain, disableAutoFill = false, disablePasting = false }: Props) {
    const checkTimeoutHandle = useRef<NodeJS.Timeout>();

    const appParameters = useAppParameters();

    const t = useTranslations();

    const autoComplete = disableAutoFill ? 'off' : undefined;

    const [waiting, setWaiting] = useState(false);
    const [token, setToken] = useState<string | null>(null);
    const [controlCode, setControlCode] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [formState, formAction] = useFormState(loginWithSmartId, { status: FormStatus.Pending });

    const handleReset = useCallback(() => {
        setToken(null);
        setControlCode(null);
        setWaiting(false);
    }, []);

    const handleCancel = useCallback(
        (message: string | null) => {
            setErrorMessage(message);

            handleReset();
        },
        [handleReset],
    );

    const handleTimeElapsed = useCallback(() => {
        handleCancel('auth.smartIdTimeElapsed');
    }, [handleCancel]);

    const handleCheck = useCallback(async () => {
        if (token == null) {
            return handleCancel('auth.smartIdError');
        }

        const result = await getSmartIdStatus(token);

        if (result === FormStatus.Pending) {
            checkTimeoutHandle.current = setTimeout(handleCheck, appParameters.smartId.checkInterval);

            return;
        }

        if (result === FormStatus.Success) {
            return onSuccess?.();
        }

        if (result === FormStatus.AdminRestrictedError) {
            handleCancel('auth.adminRestrictionError');

            return;
        }

        handleCancel('auth.smartIdError');
    }, [appParameters, handleCancel, onSuccess, token]);

    const handleCancelClick = useCallback(() => {
        handleCancel(null);
    }, [handleCancel]);

    useEffect(() => {
        setErrorMessage(null);

        if (formState.status === FormStatus.Success) {
            setToken(formState.data?.token ?? null);
            setControlCode(formState.data?.controlCode ?? null);
            setWaiting(true);
        }
    }, [formState]);

    useChangeEffect(() => {
        if (waiting) {
            setErrorMessage(null);

            checkTimeoutHandle.current = setTimeout(handleCheck, appParameters.smartId.checkInterval);

            return;
        }

        if (checkTimeoutHandle.current != null) {
            clearTimeout(checkTimeoutHandle.current);
        }
    }, waiting);

    useEffect(() => {
        return () => {
            if (checkTimeoutHandle.current != null) {
                clearInterval(checkTimeoutHandle.current);
            }
        };
    }, []);

    return (
        <>
            {!waiting && (
                <form action={formAction} className="flex flex-col gap-8">
                    <div className="flex flex-col gap-6">
                        {formState.status === FormStatus.Error && <Message>{t(formState.message)}</Message>}
                        {errorMessage != null && <Message>{t(errorMessage)}</Message>}

                        <Input
                            noSpaces
                            name="idCode"
                            label={t('auth.idCodeLabel')}
                            placeholder={t('auth.idCodePlaceholder')}
                            autoComplete={autoComplete}
                            disabledPasting={disablePasting}
                            error={formState.violations?.idCode}
                        />

                        <Select
                            name="country"
                            label={t('auth.countryLabel')}
                            placeholder={t('auth.countryPlaceholder')}
                            options={[
                                {
                                    value: 'LT',
                                    label: t('auth.countries.lithuania'),
                                },
                                {
                                    value: 'LV',
                                    label: t('auth.countries.latvia'),
                                },
                                {
                                    value: 'EE',
                                    label: t('auth.countries.estonia'),
                                },
                            ]}
                            defaultValue={domain ?? ''}
                            error={formState.violations?.country}
                        />
                    </div>

                    <Button>{t('auth.login')}</Button>
                </form>
            )}

            {waiting && (
                <div className="flex flex-col gap-8">
                    <div className="flex flex-col items-center gap-1">
                        <div className="text-center">
                            <div>{t('auth.smartIdYourControlCode')}</div>
                            <div className="text-2xl font-medium">{controlCode}</div>
                        </div>

                        <div className="flex flex-col justify-center gap-2 p-4 text-center text-primary-500">
                            <SpinnerIcon className="size-8 animate-spin" />
                            <Countdown
                                duration={appParameters.smartId.waitDuration}
                                onTimeElapsed={handleTimeElapsed}
                            />
                        </div>

                        <div className="text-center text-neutral-500">{t('auth.smartIdInstructions')}</div>
                    </div>

                    <Button variant="outlined" color="neutral" onClick={handleCancelClick}>
                        {t('general.cancel')}
                    </Button>
                </div>
            )}
        </>
    );
}
